import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';

type PriceDialogProps = {
  open: boolean;
  onClose: () => void;
  // Starting amount: the known fixed prices on the booking.
  initialPrice: number;
  // "complete": Save & complete / Complete without price. "edit": change the price.
  mode: 'complete' | 'edit';
  busy?: boolean;
  onSave: (finalPrice: number) => void;
  // Complete mode only: finish without a price (it then shows in Analytics as missing).
  onSkip?: () => void;
};

// The final price for a booking, mainly for "Price varies" services. Used when
// completing a booking, from the booking's "Edit price", and the Analytics list.
const PriceDialog: React.FC<PriceDialogProps> = ({ open, onClose, initialPrice, mode, busy = false, onSave, onSkip }) => {
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText(initialPrice > 0 ? String(initialPrice) : '');
  }, [open, initialPrice]);
  const value = Number(text);
  const valid = text.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 100000;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 900 }}>{mode === 'complete' ? 'Complete booking' : 'Edit price'}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {mode === 'complete' ?
            'Enter the final price for this booking. You can also add it later from Analytics.' :
            'Set what this booking actually cost. Revenue and the customer’s total update to match.'}
        </Typography>
        <TextField autoFocus fullWidth label="Final price" value={text} disabled={busy} inputMode="decimal"
          placeholder="0.00"
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d.]/g, '');
            if (/^\d*(\.\d{0,2})?$/.test(next)) setText(next);
          }}
          onKeyDown={(event) => { if (event.key === 'Enter' && valid && !busy) onSave(value); }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        {mode === 'complete' && onSkip ? (
          <Button onClick={onSkip} disabled={busy} sx={{ mr: 'auto' }}>Complete without price</Button>
        ) : <Button onClick={onClose} disabled={busy} sx={{ mr: 'auto' }}>Cancel</Button>}
        <Button variant="contained" disabled={!valid || busy} onClick={() => onSave(value)} sx={{ fontWeight: 800 }}>
          {busy ? 'Saving…' : mode === 'complete' ? 'Save & complete' : 'Save price'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceDialog;
